import { useEffect, useRef } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { fetchMessagesForRoom, subscribeToMessagesForRoom } from "./ChatThunk";
import * as chatV2 from "../../../modules/chat/redux/ChatSlice.v2";
import { RootState } from "src/setup/redux/store";
import { Room } from "src/app/models/Chat.v2";

export const useRoomMessagesSubscriber = () => {
  const dispatch = useDispatch();
  const messageUnsubRef = useRef<() => void>();
  const lastFetchedAtRef = useRef<Record<string, number>>({});
  const EXPIRY_MS = 60 * 1000 * 3; // 3 minute

  const { activeTab } = useSelector((state: RootState) => state.ChatV2);
  const { collaborationsById, selectedCollaborationId, selectedRoomId } =
    useSelector(
      (state: RootState) =>
        state.ChatV2[activeTab] ?? {
          collaborationOrder: [],
          collaborationsById: {},
          hasMore: false,
          isLoading: false,
          selectedCollaborationId: null,
          selectedRoomId: null,
        },
      shallowEqual
    );

  const selectedRoom: Room | undefined =
    collaborationsById?.[selectedCollaborationId]?.roomsModel?.find(
      (r) => r.id === selectedRoomId
    ) ?? collaborationsById?.[selectedCollaborationId]?.roomsModel?.[0];

  const messages = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.messages ?? []
  );

  useEffect(() => {
    if (!selectedCollaborationId || !selectedRoomId || !selectedRoom) return;

    const key = `${selectedCollaborationId}_${selectedRoomId}`;
    const alreadyFetched = Array.isArray(messages) && messages.length > 0;
    const lastFetchedAt = lastFetchedAtRef.current[key] ?? 0;
    const now = Date.now();
    const isExpired = now - lastFetchedAt > EXPIRY_MS;

    if (!alreadyFetched || isExpired) {
      dispatch(
        fetchMessagesForRoom({
          collaborationId: selectedCollaborationId,
          roomId: selectedRoomId,
          conversationId: selectedRoom?.conversationId,
        })
      );
    }

    if (messageUnsubRef.current) {
      messageUnsubRef.current();
    }

    const unsub = subscribeToMessagesForRoom(
      selectedCollaborationId,
      selectedRoomId,
      selectedRoom.conversationId,
      (message, type) => {
        if (type === "added") {
          dispatch(
            chatV2.addNewMessage({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              message,
            })
          );
        } else if (type === "modified") {
          dispatch(
            chatV2.updateMessage({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              message,
            })
          );
        }
      }
    );

    messageUnsubRef.current = unsub;

    return () => {
      if (messageUnsubRef.current) {
        messageUnsubRef.current();
        messageUnsubRef.current = undefined;
      }
    };
  }, [
    dispatch,
    selectedCollaborationId,
    selectedRoomId,
    selectedRoom?.conversationId,
  ]);

  useEffect(() => {
    const key = `${selectedCollaborationId}_${selectedRoomId}`;
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        selectedCollaborationId &&
        selectedRoomId &&
        selectedRoom
      ) {
        const now = Date.now();
        const lastFetchedAt = lastFetchedAtRef.current[key] ?? 0;
        const isExpired = now - lastFetchedAt > EXPIRY_MS;

        if (isExpired) {
          dispatch(
            fetchMessagesForRoom({
              collaborationId: selectedCollaborationId,
              roomId: selectedRoomId,
              conversationId: selectedRoom.conversationId,
            })
          );
          lastFetchedAtRef.current[key] = now;
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    dispatch,
    selectedCollaborationId,
    selectedRoomId,
    selectedRoom?.conversationId,
  ]);
};
